import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, BadgeCheck, Briefcase, Check, Clock, Coins, FileSignature, FileText, Scale } from 'lucide-react';
import { api, type RegelsOverzicht } from '../lib/api';
import { cn, formatDate, formatMoney } from '../lib/utils';
import { Card, Disclosure, ICON, Kpi, Notice, PageHeader, PageSkeleton, Segmented, Tabs } from '../components/ui';

// =====================================================================
// Regels en cijfers. Van boven naar beneden:
//   1. Nu: je loon, contract, uren en functie volgens AFAS, en of de app daarmee rekent;
//   2. je uurloon door de tijd (AFAS);
//   3. de cao-regels waarmee de app rekent, in gewone taal, elk nagekeken tegen de cao;
//   4. certificaten en passen (AFAS + documenten);
//   5. alle ruwe gegevens uit je documenten, ingeklapt.
// =====================================================================

type Cert = RegelsOverzicht['certificaten'][number];

const STATUS: Record<Cert['status'], { cls: string; tekst: string }> = {
  geldig: { cls: 'status-ok', tekst: 'Geldig' },
  verloopt: { cls: 'status-warn', tekst: 'Verloopt binnenkort' },
  verlopen: { cls: 'status-err', tekst: 'Verlopen' },
  vervangen: { cls: 'status-muted', tekst: 'Vervangen door nieuwere' },
  blijvend: { cls: 'status-ok', tekst: 'Blijft geldig' },
};

/** Cao-regels in gewone taal, in deze volgorde en groepen. */
const CAO_REGELS: Array<{ groep: string; regels: Array<{ sleutel: string; tekst: string; waarde: (v: string) => string }> }> = [
  {
    groep: 'Toeslagen op je uurloon',
    regels: [
      { sleutel: 'toeslag_avond', tekst: 'Avond, ma t/m vr 18:00–24:00', waarde: (v) => `+${v}` },
      { sleutel: 'toeslag_nacht', tekst: 'Nacht, ma t/m vr 00:00–07:00', waarde: (v) => `+${v}` },
      { sleutel: 'toeslag_weekend', tekst: 'Zaterdag en zondag', waarde: (v) => `+${v}` },
      { sleutel: 'toeslag_feest', tekst: 'Feestdag', waarde: (v) => `+${v}` },
      { sleutel: 'toeslag_oudjaar', tekst: 'Oudejaarsdag na 16:00', waarde: (v) => `+${v}` },
      { sleutel: 'toeslag_overwerk', tekst: 'Overwerk', waarde: (v) => `+${v}` },
    ],
  },
  {
    groep: 'Uren',
    regels: [
      { sleutel: 'fulltime_uren', tekst: 'Fulltime', waarde: (v) => `${v} uur per periode` },
      { sleutel: 'overwerk_grens', tekst: 'Overwerk telt vanaf', waarde: (v) => `${v} uur per periode` },
    ],
  },
  {
    groep: 'Verlof',
    regels: [{ sleutel: 'vakantie_uren', tekst: 'Vakantie-opbouw (fulltime)', waarde: (v) => `${v.replace(' uur', '')} uur per periode` }],
  },
  {
    groep: 'Uitkeringen en vergoedingen',
    regels: [
      { sleutel: 'vakantiegeld', tekst: 'Vakantiegeld, uiterlijk in juni', waarde: (v) => v },
      { sleutel: 'eju', tekst: 'Eindejaarsuitkering', waarde: (v) => v },
      { sleutel: 'km_vergoeding', tekst: 'Reiskosten woon-werk', waarde: (v) => `€ ${v.replace(' €', '')} per km` },
      { sleutel: 'km_vanaf', tekst: 'Reiskosten vanaf', waarde: (v) => `${v} km enkele reis` },
    ],
  },
];

function Vinkje({ klopt, app }: { klopt: boolean; app?: string | null }) {
  return klopt ? (
    <span className="inline-flex items-center gap-1 text-xs text-money" title="De app rekent hiermee">
      <Check className="h-3.5 w-3.5" aria-hidden />
      <span className="sr-only">Klopt met de app</span>
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs text-amber-200">
      <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
      App: {app ?? 'onbekend'}
    </span>
  );
}

function BronLink({ id, titel }: { id: number; titel: string }) {
  return (
    <a
      href={api.documents.fileUrl(id)}
      target="_blank"
      rel="noreferrer"
      className="inline-flex min-w-0 items-center gap-1 text-xs text-slate-400 underline-offset-2 hover:text-slate-100 hover:underline"
    >
      <FileText className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="truncate">{titel}</span>
    </a>
  );
}

/** "BEVEILIGER A" → "Beveiliger A", "ONBEPAALDE TIJD" → "Onbepaalde tijd": korte codes (A, AB) blijven hoofdletters. */
const hoofdletter = (s: string | null | undefined) =>
  s
    ? s
        .toLowerCase()
        .split(' ')
        .map((w, i) => (w.length <= 2 ? w.toUpperCase() : i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
        .join(' ')
    : '–';

function redenTekst(reden: string | null, functie: string | null): string {
  switch ((reden ?? '').toLowerCase()) {
    case 'salaris':
      return 'Loonsverhoging (cao of trede)';
    case 'functie':
      return `Nieuwe functie: ${hoofdletter(functie)}`;
    case 'contract':
      return 'Nieuw contract';
    default:
      return reden ?? '–';
  }
}

export function RegelsPage() {
  const { data, isLoading, error } = useQuery({ queryKey: ['regels'], queryFn: api.regels });
  const [alleCerts, setAlleCerts] = useState(false);

  if (isLoading) return <PageSkeleton kpis={4} />;
  if (error || !data)
    return (
      <div className="page">
        <PageHeader title="Regels en cijfers" />
        <Notice tone="err">De regels konden niet worden geladen. Ververs de pagina.</Notice>
      </div>
    );

  const nu = data.nu;
  const perSleutel = new Map(data.onderwerpen.flatMap((o) => o.vergelijkingen).map((v) => [v.sleutel, v]));
  const caoAfwijkend = CAO_REGELS.flatMap((g) => g.regels).filter((r) => perSleutel.get(r.sleutel)?.gelijk === false).length;
  const afwijkend = caoAfwijkend + (nu ? [nu.uurloon.klopt, nu.contract?.klopt ?? true, nu.uren?.klopt ?? true].filter((k) => !k).length : 0);

  const bijzaak = (c: Cert) => c.status === 'vervangen' || (c.status === 'blijvend' && c.type === 'Certificaat');
  const certs = data.certificaten.filter((c) => alleCerts || !bijzaak(c));
  const verborgen = data.certificaten.filter(bijzaak).length;

  return (
    <div className="page">
      <PageHeader
        title="Regels en cijfers"
        sub="Je loon en contract volgens AFAS, de cao-regels waarmee de app rekent, en je certificaten. Een vinkje betekent: de app rekent precies zo."
      />

      {afwijkend > 0 ? (
        <Notice tone="warn">
          {afwijkend === 1 ? 'Eén ding klopt' : `${afwijkend} dingen kloppen`} niet met de app. Ze staan hieronder met een waarschuwing.
        </Notice>
      ) : (
        <Notice tone="ok">Alles klopt: de app rekent met hetzelfde loon, contract en dezelfde cao-regels als AFAS en je documenten.</Notice>
      )}

      {nu && (
        <section className="space-y-2" aria-labelledby="nu-kop">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 id="nu-kop" className="t-section">Nu, volgens AFAS</h2>
            {nu.bron_datum && <span className="text-xs text-slate-400">Opgehaald {formatDate(nu.bron_datum.slice(0, 10))}</span>}
          </div>
          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            <Kpi
              icon={Coins}
              label="Uurloon"
              text={formatMoney(nu.uurloon.waarde)}
              tone="money"
              rows={[
                ['Schaal', `${nu.uurloon.schaal ?? '–'}, trede ${nu.uurloon.trede ?? '–'}`],
                ['Sinds', formatDate(nu.uurloon.sinds)],
                ['App', <Vinkje key="v" klopt={nu.uurloon.klopt} app={nu.uurloon.app != null ? formatMoney(nu.uurloon.app) : null} />],
              ]}
            />
            <Kpi
              icon={FileSignature}
              label="Contract"
              text={nu.contract ? (/onbepaald/i.test(nu.contract.soort ?? '') ? 'Vast' : 'Tijdelijk') : '–'}
              rows={
                nu.contract
                  ? [
                      ['Soort', hoofdletter(nu.contract.soort)],
                      ['Sinds', formatDate(nu.contract.sinds)],
                      nu.contract.tot ? ['Tot', formatDate(nu.contract.tot)] : false,
                      ['App', <Vinkje key="v" klopt={nu.contract.klopt} app={nu.contract.app ? `${nu.contract.app} tijd` : null} />],
                    ]
                  : undefined
              }
            />
            <Kpi
              icon={Clock}
              label="Contracturen"
              text={nu.uren?.per_periode != null ? `${nu.uren.per_periode} uur` : '–'}
              rows={
                nu.uren
                  ? [
                      ['Per week', `${nu.uren.per_week ?? '–'} uur`],
                      ['Fulltime', nu.uren.parttime_pct === 100 ? 'Ja' : `${nu.uren.parttime_pct ?? '–'}%`],
                      ['App', <Vinkje key="v" klopt={nu.uren.klopt} app={nu.uren.app != null ? `${nu.uren.app} uur` : null} />],
                    ]
                  : undefined
              }
              hint={nu.uren ? 'Per periode van 4 weken' : undefined}
            />
            <Kpi
              icon={Briefcase}
              label="Functie"
              text={hoofdletter(nu.functie?.naam)}
              rows={nu.functie ? [['Team', nu.functie.afdeling?.replace(/^Mdw\s+/, '') ?? '–'], ['Sinds', formatDate(nu.functie.sinds)]] : undefined}
            />
          </div>
        </section>
      )}

      {data.loongeschiedenis.length > 0 && (
        <Card flush title="Je uurloon door de tijd" icon={Coins}>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Vanaf</th>
                  <th className="r">Uurloon</th>
                  <th>Schaal</th>
                  <th className="hidden sm:table-cell">Waarom</th>
                  <th className="r">App</th>
                </tr>
              </thead>
              <tbody>
                {data.loongeschiedenis.map((r, i) => (
                  <tr key={r.van} className={cn(i === 0 ? 'text-slate-50' : 'text-slate-300')}>
                    <td className="tabular-nums">{formatDate(r.van)}</td>
                    <td className="r font-medium">{formatMoney(r.uurloon)}</td>
                    <td className="whitespace-nowrap">
                      {r.schaal ?? '–'}, trede {r.trede ?? '–'}
                    </td>
                    <td className="hidden text-slate-400 sm:table-cell">{redenTekst(r.reden, r.functie)}</td>
                    <td className="r">
                      <Vinkje klopt={r.klopt} app={r.app_uurloon != null ? formatMoney(r.app_uurloon) : null} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card
        flush
        title="Cao-regels waarmee de app rekent"
        icon={Scale}
        action={data.cao ? <BronLink id={data.cao.document_id} titel="Lees de cao" /> : undefined}
      >
        <div className="grid gap-x-8 border-t border-surface-line/60 lg:grid-cols-2">
          {CAO_REGELS.map((g) => {
            const regels = g.regels.flatMap((r) => {
              const v = perSleutel.get(r.sleutel);
              return v ? [{ ...r, v }] : [];
            });
            if (!regels.length) return null;
            return (
              <div key={g.groep} className="py-2">
                <h3 className="px-4 pb-1 pt-2 text-xs font-medium text-slate-400 sm:px-5">{g.groep}</h3>
                <ul>
                  {regels.map(({ sleutel, tekst, waarde, v }) => (
                    <li key={sleutel} className="flex min-h-[40px] items-center gap-3 px-4 py-2 sm:px-5">
                      <span className="min-w-0 flex-1 text-sm text-slate-200">{tekst}</span>
                      <span className="shrink-0 text-sm font-medium tabular-nums text-slate-50">{waarde(v.document)}</span>
                      <span className="flex w-16 shrink-0 justify-end">
                        <Vinkje klopt={v.gelijk} app={v.app} />
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </Card>

      <Card
        flush
        title="Certificaten en passen"
        icon={BadgeCheck}
        action={
          verborgen > 0 && (
            <button type="button" className="btn-ghost btn-sm" onClick={() => setAlleCerts((v) => !v)}>
              {alleCerts ? 'Alleen belangrijke' : `Alle trainingen (+${verborgen})`}
            </button>
          )
        }
      >
        <div className="list">
          {certs.map((c) => (
            <div key={c.id} className="row">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-slate-100">{c.naam}</div>
                <div className="truncate text-xs text-slate-400">
                  {c.type}
                  {c.behaald_op ? `, behaald ${formatDate(c.behaald_op)}` : ''}
                  {c.uitgever === 'AFAS' ? ', uit AFAS' : ''}
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className={STATUS[c.status].cls}>{STATUS[c.status].tekst}</div>
                {c.geldig_tot && <div className="text-xs tabular-nums text-slate-400">tot {formatDate(c.geldig_tot)}</div>}
              </div>
              {c.source_document_id ? (
                <a
                  href={api.documents.fileUrl(c.source_document_id)}
                  target="_blank"
                  rel="noreferrer"
                  className="icon-btn shrink-0"
                  aria-label={`Open ${c.naam}`}
                  title="Open document"
                >
                  <FileText className={ICON.sm} aria-hidden />
                </a>
              ) : (
                <span className="w-10 shrink-0" aria-hidden />
              )}
            </div>
          ))}
        </div>
      </Card>

      <Card flush>
        <Disclosure title="Alle gegevens uit je documenten" meta={`${data.documenten} gelezen documenten, per onderwerp`}>
          <DocumentFeiten data={data} />
        </Disclosure>
      </Card>
    </div>
  );
}

type DocFeiten = { titel: string; datum: string | null; geldend: boolean; regels: Array<{ label: string; waarde: string }> };

/** Per onderwerp de feiten per document, ingekort; alleen geldende documenten tenzij gevraagd. */
function DocumentFeiten({ data }: { data: RegelsOverzicht }) {
  const [tab, setTab] = useState(data.onderwerpen[0]?.key ?? 'loon');
  const [toon, setToon] = useState<'geldend' | 'alles'>('geldend');
  const onderwerp = data.onderwerpen.find((o) => o.key === tab) ?? data.onderwerpen[0];
  const perDocument = useMemo(() => {
    const map = new Map<number, DocFeiten>();
    for (const f of onderwerp?.feiten ?? []) {
      if (toon === 'geldend' && !f.geldend) continue;
      const d = map.get(f.document_id) ?? { titel: f.titel, datum: f.datum, geldend: f.geldend, regels: [] };
      d.regels.push({ label: f.label, waarde: f.waarde });
      map.set(f.document_id, d);
    }
    return [...map.entries()];
  }, [onderwerp, toon]);

  if (!onderwerp) return <p className="t-muted">Nog geen gelezen documenten.</p>;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <Tabs items={data.onderwerpen.map((o) => ({ value: o.key, label: o.label }))} value={onderwerp.key} onChange={setTab} className="min-w-0 flex-1" />
        <Segmented
          label="Welke documenten"
          value={toon}
          onChange={setToon}
          items={[
            { value: 'geldend', label: 'Geldend' },
            { value: 'alles', label: 'Ook verlopen' },
          ]}
        />
      </div>
      {perDocument.length === 0 && <p className="t-muted">Geen geldende documenten over dit onderwerp.</p>}
      <div className="divide-y divide-surface-line/60">
        {perDocument.map(([id, d]) => (
          <DocumentBlok key={id} id={id} d={d} />
        ))}
      </div>
    </div>
  );
}

function DocumentBlok({ id, d }: { id: number; d: DocFeiten }) {
  const [open, setOpen] = useState(false);
  const KORT = 4;
  return (
    <div className={cn('py-3', !d.geldend && 'opacity-70')}>
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <BronLink id={id} titel={d.titel} />
        </div>
        <span className="shrink-0 text-xs text-slate-500">{d.datum ? formatDate(d.datum) : ''}</span>
      </div>
      <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        {(open ? d.regels : d.regels.slice(0, KORT)).map((r, i) => (
          <div key={i} className="contents">
            <dt className="text-xs text-slate-400 sm:text-sm">{r.label}</dt>
            <dd className="text-sm text-slate-100">{r.waarde}</dd>
          </div>
        ))}
      </dl>
      {d.regels.length > KORT && (
        <button type="button" className="mt-1 text-xs text-slate-400 hover:text-slate-100" onClick={() => setOpen((v) => !v)}>
          {open ? 'Minder' : `Nog ${d.regels.length - KORT}`}
        </button>
      )}
    </div>
  );
}
